import { useState } from "react";

import { toErrorMessage } from "../../../../components/Input/InstrumentSearch";
import { notify } from "../../../../components/Notification";
import {
  useDeferCorporateActionMutation,
  useReconcileCorporateActionMutation,
  useResumeCorporateActionMutation,
} from "../../../../services/admin";
import { OpenActionItem } from "../types";
import { decisionDetailsOf } from "../utils/toWorkItem";
import { useWorkQueueSession } from "../WorkQueueProvider";

export const useCorporateActionActions = () => {
  const [deferMutation] = useDeferCorporateActionMutation();
  const [resumeMutation] = useResumeCorporateActionMutation();
  const [reconcileMutation] = useReconcileCorporateActionMutation();
  const { recordDecision, setFailure } = useWorkQueueSession();
  const [isActing, setIsActing] = useState(false);

  const whileActing = async (
    { key, symbol }: OpenActionItem,
    action: () => Promise<void>,
  ) => {
    setFailure(null);
    setIsActing(true);
    try {
      await action();
    } catch (err: unknown) {
      setFailure({ key, symbol, message: toErrorMessage(err) });
    } finally {
      setIsActing(false);
    }
  };

  const deferAction = (item: OpenActionItem) =>
    whileActing(item, async () => {
      await deferMutation(item.action.id).unwrap();
      notify.success(
        `${item.symbol} deferred. Find it with the Deferred filter.`,
      );
    });

  const resumeAction = (item: OpenActionItem) =>
    whileActing(item, async () => {
      await resumeMutation(item.action.id).unwrap();
      notify.success(`${item.symbol} returned to pending.`);
    });

  const reconcileAction = (item: OpenActionItem) =>
    whileActing(item, async () => {
      await reconcileMutation(item.action.id).unwrap();
      recordDecision({
        key: item.key,
        kind: "corporate",
        action: item.action,
        summary: `${item.symbol} reconciled: ${decisionDetailsOf(item.action)}`,
        resolvedAt: new Date().toISOString(),
      });
    });

  return { isActing, deferAction, resumeAction, reconcileAction };
};
